/**
 * Note type picker for account notes — ported from synrgyinfinity's
 * TypeSelector. A note records what kind of interaction it captures (plain
 * note, call, meeting, email, or task); the type is purely presentational.
 *
 * Mirrors the demo's display rules: the composer offers all five types, and
 * saved notes show a type pill only when the type is something other than a
 * plain Note.
 */

import React from 'react';
import {CheckSquare, Mail, MessageSquare, Phone, Video} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@common-web/components/ui/dropdown-menu';
import {cn} from '@common-web/lib/utils';

export type NoteType = 'NOTE' | 'CALL' | 'MEETING' | 'EMAIL' | 'TASK';

interface NoteTypeMeta {
  label: string;
  icon: typeof MessageSquare;
}

export const NOTE_TYPE_META: Record<NoteType, NoteTypeMeta> = {
  NOTE: {label: 'Note', icon: MessageSquare},
  CALL: {label: 'Call', icon: Phone},
  MEETING: {label: 'Meeting', icon: Video},
  EMAIL: {label: 'Email', icon: Mail},
  TASK: {label: 'Task', icon: CheckSquare},
};

const NOTE_TYPE_OPTIONS: NoteType[] = ['NOTE', 'CALL', 'MEETING', 'EMAIL', 'TASK'];

interface NoteTypeSelectProps {
  value: NoteType;
  onChange: (value: NoteType) => void;
  disabled?: boolean;
}

export function NoteTypeSelect({
  value,
  onChange,
  disabled,
}: NoteTypeSelectProps): React.ReactElement {
  const meta = NOTE_TYPE_META[value];
  const Icon = meta.icon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <button
          type='button'
          disabled={disabled}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition',
            'bg-muted text-foreground/80 hover:bg-muted/70',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          <Icon className='h-3.5 w-3.5' />
          {meta.label}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='start' className='min-w-[140px]'>
        {NOTE_TYPE_OPTIONS.map((option) => {
          const optMeta = NOTE_TYPE_META[option];
          const OptIcon = optMeta.icon;
          return (
            <DropdownMenuItem
              key={option}
              onSelect={() => onChange(option)}
              className={cn('flex items-center gap-2', option === value && 'bg-muted')}
            >
              <OptIcon className='h-3.5 w-3.5 shrink-0' />
              <span className='text-sm'>{optMeta.label}</span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Compact type badge on saved notes. Plain notes render nothing (matching the
 * demo's TypePill) so the feed isn't cluttered with a redundant "Note" badge.
 */
export function NoteTypePill({type}: {type: NoteType}): React.ReactElement | null {
  if (type === 'NOTE') return null;
  const meta = NOTE_TYPE_META[type];
  const Icon = meta.icon;
  return (
    <span className='inline-flex items-center gap-1 whitespace-nowrap rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground'>
      <Icon className='h-2.5 w-2.5' />
      {meta.label}
    </span>
  );
}
